import { DimensionValue, StyleProp, View, ViewStyle } from 'react-native';
import { useElementPosition } from '../../../hooks/useElementPosition';
import { useTableCellDimensions } from '../../../hooks/useTableCellDimensions';
import { RichTableState, SelectedCategories, TableActionOrGroup, TableConfig } from './types';
import Stack, { contentStackSpacing } from '../Stack';
import Text from '../Text';
import { colors } from '../../../utils/theme';
import React, { useMemo } from 'react';
import { TABLE_PADDING, filterAndSortData } from './utils';
import TableColumnHeader from './TableColumnHeader';
import PressableDynamicOpacity from '../PressableDynamicOpacity';
import { Paper } from '../Paper';
import SelectionCheckbox from './SelectionCheckbox';
import SearchFilters from './SearchFilters';
import { useResetSelection } from './useResetSelection';
import TableActions from './TableActions';
import { getIntl } from '../../../utils/intl';
import FadeIn from '../FadeIn';
import IndeterminateLoadingBar from '../../shared/IndeterminateLoadingBar';
import { getSpacing } from '../../../utils/spacing';
import IsDisabledProvider from '../IsDisabledProvider';
import { TableRow } from './TableRow';
import { useIsMobileLayout } from '../../../hooks/useIsMobileLayout';
import { useIsInSideTabContentContainer } from '../TabView';
import SelectedCategoriesDisplay from './SelectedCategoriesDisplay';
import { PRFlatList, PRScrollView } from '../../shared/Scroll';

const intl = getIntl('RichTable', {
    noData: 'No data found',
    noDataFiltered: 'No data found for the filter',
});

type MinWidth = DimensionValue | { mobile: DimensionValue; desktop: DimensionValue };

export type RichTableOwnProps<TDataElement> = {
    config: TableConfig<TDataElement>;
    style?: StyleProp<ViewStyle>;
    data: TDataElement[];
    state: RichTableState;
    onStateChange: (newState: RichTableState) => void;
    minWidth?: MinWidth;
    onRowClick?: (row: TDataElement) => void;
    actions?: TableActionOrGroup<TDataElement>[];
    allowRowSelection?: boolean;
    emptyStateMessage?: string;
    noPaper?: boolean;
    noPaperPadding?: boolean;
    isFetching?: boolean;
    headerRight?: React.ReactNode;
    selectedCategories?: SelectedCategories;
    testID?: string;
    renderNoActionsPlaceholder?: () => React.ReactNode;
};

export type RichTableProps<TDataElement> = RichTableOwnProps<TDataElement>;

const tableRowPadding = 8;

const getMinWidth = (minWidth: MinWidth | undefined, isMobile: boolean) => {
    if (!minWidth) {
        return undefined;
    }

    if (typeof minWidth === 'string' || typeof minWidth === 'number') {
        return minWidth;
    }

    if ('mobile' in minWidth) {
        return isMobile ? minWidth.mobile : minWidth.desktop;
    }

    return minWidth;
};

function RichTable<TDataElement>(props: RichTableProps<TDataElement>) {
    const isInSideTabContentContainer = useIsInSideTabContentContainer();
    const { ref, position, onLayout } = useElementPosition({});
    const isMobile = useIsMobileLayout();
    const minWidth = getMinWidth(props.minWidth, isMobile);
    const { getCellStyle, getTableCellLayoutHandler } = useTableCellDimensions({
        tableWidth: position?.width || null,
        columnCount: props.config.columns.length,
        minWidth: minWidth || null,
    });

    const filteredAndSortedData = useMemo(
        () => filterAndSortData(props.config, props.data, props.state),
        [props.config, props.data, props.state],
    );

    useResetSelection({
        state: props.state,
        onStateChange: props.onStateChange,
        data: props.data,
    });

    const renderRow = (row: TDataElement, rowIndex: number) => (
        <TableRow
            key={props.config.getRowKey(row)}
            row={row}
            allData={props.data}
            rowIndex={rowIndex}
            getTableCellLayoutHandler={getTableCellLayoutHandler}
            config={props.config}
            state={props.state}
            onStateChange={props.onStateChange}
            allowRowSelection={!!props.allowRowSelection}
            tableRowPadding={tableRowPadding}
            getCellStyle={getCellStyle}
        />
    );

    const renderTableContent = () => {
        const renderMessage = (message: string, testID: string) => (
            <Stack horizontal fullWidth justify="center" style={{ padding: tableRowPadding }} testID={testID}>
                <Text variant="body-1" color="Gray-Text">
                    {message}
                </Text>
            </Stack>
        );

        if (!props.data.length) {
            return renderMessage(props.emptyStateMessage || intl('noData'), 'empty-state-message');
        } else if (!filteredAndSortedData.length) {
            return renderMessage(intl('noDataFiltered'), 'empty-state-message');
        } else {
            return (
                <PRFlatList
                    style={{ width: '100%', flex: 1, flexGrow: 1 }}
                    testID="rich-table-flat-list"
                    data={filteredAndSortedData}
                    showsVerticalScrollIndicator={true}
                    renderItem={({ item, index }) =>
                        props.onRowClick ? (
                            <PressableDynamicOpacity
                                onPress={() => props.onRowClick?.(item)}
                                key={props.config.getRowKey(item)}
                                style={{ backgroundColor: 'purple' }}
                            >
                                {renderRow(item, index)}
                            </PressableDynamicOpacity>
                        ) : (
                            renderRow(item, index)
                        )
                    }
                />
            );
        }
    };

    const hasAnySearchColumns = props.config.columns.some((column) => !!column.getSearchValue);

    const displayCategories = (props.selectedCategories || []).filter((_) => _.count);

    const table = (
        <IsDisabledProvider isDisabled={!!props.isFetching}>
            <Stack vertical fullHeight fullWidth>
                {props.actions?.length || props.renderNoActionsPlaceholder || props.headerRight ? (
                    <View style={{ width: '100%', marginBottom: getSpacing(contentStackSpacing) }}>
                        <TableActions
                            state={props.state}
                            actions={props.actions || []}
                            data={props.data}
                            getRowKey={props.config.getRowKey}
                            headerRight={props.headerRight}
                            renderNoActionsPlaceholder={props.renderNoActionsPlaceholder}
                        />
                    </View>
                ) : null}
                {hasAnySearchColumns || displayCategories?.length ? (
                    <PRScrollView
                        horizontal
                        style={{ width: '100%', flexGrow: 0 }}
                        contentContainerStyle={{ minWidth: minWidth || '100%' }}
                        testID={props.testID}
                    >
                        <Stack
                            horizontal
                            fullWidth
                            style={{ marginBottom: getSpacing(contentStackSpacing) - 3 }}
                            space={2}
                            justify="flex-start"
                            align="center"
                        >
                            {hasAnySearchColumns ? (
                                <SearchFilters
                                    data={props.data}
                                    config={props.config}
                                    state={props.state}
                                    onStateChange={props.onStateChange}
                                />
                            ) : null}
                            {displayCategories.length ? (
                                <SelectedCategoriesDisplay selectedCategories={displayCategories} />
                            ) : null}
                        </Stack>
                    </PRScrollView>
                ) : null}
                <PRScrollView
                    horizontal
                    style={{ width: '100%', flex: 1 }}
                    contentContainerStyle={{ minWidth: minWidth || '100%' }}
                    testID={props.testID}
                >
                    <View style={{ width: '100%', minWidth: minWidth }} onLayout={onLayout} ref={ref}>
                        <FadeIn style={{ width: '100%', height: 3 }} isIn={!!props.isFetching}>
                            {props.isFetching ? (
                                <IndeterminateLoadingBar
                                    style={{ width: '100%', height: '100%' }}
                                    barStyle={{ borderRadius: 1.5 }}
                                />
                            ) : null}
                        </FadeIn>
                        <Stack vertical fullWidth style={{ maxHeight: '100%', flex: 1 }}>
                            <Stack
                                horizontal
                                fullWidth
                                style={{ paddingLeft: TABLE_PADDING, paddingRight: TABLE_PADDING }}
                            >
                                <Stack
                                    horizontal
                                    fullWidth
                                    style={{
                                        backgroundColor: colors['Background'],
                                    }}
                                    align="center"
                                >
                                    <SelectionCheckbox
                                        state={props.state}
                                        onStateChange={props.onStateChange}
                                        allowRowSelection={props.allowRowSelection}
                                        config={props.config}
                                        data={filteredAndSortedData}
                                        isHeader
                                    />
                                    {props.config.columns
                                        .filter((_) => !isMobile || !_.hideOnMobile)
                                        .map((column, index) => (
                                            <TableColumnHeader
                                                key={`${column.label}-${index}`}
                                                columnConfig={column as any}
                                                data={props.data}
                                                getCellStyle={(colIndex) => [
                                                    getCellStyle({ colIndex, isHeader: true }),
                                                    {
                                                        display: 'flex',
                                                        justifyContent: 'center',
                                                        alignItems: 'flex-start',
                                                        flexDirection: 'column',
                                                        width: 0,
                                                    },
                                                ]}
                                                getTableCellLayoutHandler={getTableCellLayoutHandler}
                                                padding={tableRowPadding}
                                                colIndex={index}
                                                onStateChange={props.onStateChange}
                                                state={props.state}
                                                isLast={index === props.config.columns.length - 1}
                                            />
                                        ))}
                                </Stack>
                            </Stack>
                            {renderTableContent()}
                        </Stack>
                    </View>
                </PRScrollView>
            </Stack>
        </IsDisabledProvider>
    );

    return props.noPaper || isInSideTabContentContainer ? (
        <View style={[{ flex: 1, width: '100%' }, props.style]}>{table}</View>
    ) : (
        <Paper style={[{ flex: 1, width: '100%', paddingTop: TABLE_PADDING }, props.style]} testID="rich-table">
            {table}
        </Paper>
    );
}

export default RichTable;
